import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Typography,
  Row,
  Col,
  Statistic,
  Tabs,
  Alert,
  Modal,
  Descriptions,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, CalendarOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import { useCalibrationSchedules, useCalibrationRecords } from '../../hooks/useCalibration';
import { CalibrationSchedule, CalibrationRecord } from '../../services/calibration.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;

const CalibrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [recordSearch, setRecordSearch] = useState('');
  const [scheduleView, setScheduleView] = useState<CalibrationSchedule | null>(null);

  const { data: schedules = [], isLoading: schedulesLoading } = useCalibrationSchedules();
  const { data: records = [], isLoading: recordsLoading } = useCalibrationRecords();

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Scheduled: 'blue',
      'In Progress': 'processing',
      Completed: 'success',
      Overdue: 'error',
      Cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getResultColor = (result: string) => {
    const colors: Record<string, string> = {
      Pass: 'success',
      Fail: 'error',
      Conditional: 'warning',
      Pending: 'default',
    };
    return colors[result] || 'default';
  };

  const scheduleColumns: ColumnsType<CalibrationSchedule> = [
    {
      title: 'Calibration Code',
      dataIndex: 'calibration_code',
      key: 'calibration_code',
    },
    {
      title: 'Asset code',
      dataIndex: 'equipment_code',
      key: 'equipment_code',
    },
    {
      title: 'Asset / equipment name',
      dataIndex: 'equipment_name',
      key: 'equipment_name',
    },
    {
      title: 'Next Due Date',
      dataIndex: 'next_calibration_date',
      key: 'next_calibration_date',
      render: (date: string | null) => (date ? dayjs(date).format('DD/MM/YYYY') : 'On first use'),
      sorter: (a, b) => dayjs(a.next_calibration_date || '2999-12-31').unix() - dayjs(b.next_calibration_date || '2999-12-31').unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Critical',
      dataIndex: 'is_critical',
      key: 'is_critical',
      render: (critical: boolean) => <Tag color={critical ? 'red' : 'default'}>{critical ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Method',
      dataIndex: 'calibration_type',
      key: 'calibration_type',
      ellipsis: true,
      render: (t: string | undefined) => t || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: unknown, row: CalibrationSchedule) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => (row.equipment_id ? navigate(`/qms/tmmde/${row.equipment_id}`) : setScheduleView(row))}
        >
          View
        </Button>
      ),
    },
  ];

  const recordColumns: ColumnsType<CalibrationRecord> = [
    {
      title: 'Calibration Number',
      dataIndex: 'calibration_number',
      key: 'calibration_number',
    },
    {
      title: 'Asset / equipment',
      key: 'asset_equipment',
      render: (_: unknown, r) =>
        r.calibration_schedule
          ? [r.calibration_schedule.equipment_code, r.calibration_schedule.equipment_name]
              .filter(Boolean)
              .join(' — ') || '—'
        : r.equipment_id
          ? r.equipment_id
          : '—',
    },
    {
      title: 'Calibration Date',
      dataIndex: 'calibration_date',
      key: 'calibration_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      render: (result: string) => <Tag color={getResultColor(result)}>{result}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Quality review',
      dataIndex: 'quality_review_status',
      key: 'quality_review_status',
      render: (v: string | undefined) => (
        <Tag color={v === 'ACCEPTED' ? 'success' : v === 'REJECTED' ? 'error' : 'warning'}>{v || 'PENDING'}</Tag>
      ),
    },
    {
      title: 'Valid Until',
      dataIndex: 'calibration_valid_until',
      key: 'calibration_valid_until',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Next Due Date',
      dataIndex: 'next_calibration_due_date',
      key: 'next_calibration_due_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'As-found',
      dataIndex: 'as_found_reading',
      key: 'as_found_reading',
      ellipsis: true,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'As-left',
      dataIndex: 'as_left_reading',
      key: 'as_left_reading',
      ellipsis: true,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: unknown, row: CalibrationRecord) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/grc/calibration/records/${row.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  const stats = {
    totalSchedules: schedules.length,
    overdue: schedules.filter((s) => s.status === 'Overdue').length,
    totalRecords: records.length,
    passed: records.filter((r) => r.result === 'Pass').length,
  };

  const tabItems = [
    {
      key: 'schedules',
      label: (
        <span>
          <CalendarOutlined /> Calibration Schedules
        </span>
      ),
      children: (
        <div>
          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
            <Search
              placeholder="Search schedules..."
              allowClear
              style={{ width: 300 }}
              onSearch={setScheduleSearch}
              onChange={(e) => setScheduleSearch(e.target.value)}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/grc/calibration/schedules/new')}>
              Create Schedule
            </Button>
          </Space>
          <Table
            columns={scheduleColumns}
            dataSource={schedules.filter((s) => {
              const q = scheduleSearch.toLowerCase();
              return (
                (s.calibration_code || '').toLowerCase().includes(q) ||
                (s.equipment_name || '').toLowerCase().includes(q) ||
                (s.equipment_code || '').toLowerCase().includes(q)
              );
            })}
            loading={schedulesLoading}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </div>
      ),
    },
    {
      key: 'records',
      label: (
        <span>
          <FileTextOutlined /> Calibration Records
        </span>
      ),
      children: (
        <div>
          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
            <Search
              placeholder="Search records..."
              allowClear
              style={{ width: 300 }}
              onSearch={setRecordSearch}
              onChange={(e) => setRecordSearch(e.target.value)}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/grc/calibration/records/new')}>
              Create Record
            </Button>
          </Space>
          <Table
            columns={recordColumns}
            dataSource={records.filter((r) => {
              const q = recordSearch.toLowerCase();
              return (
                (r.calibration_number || '').toLowerCase().includes(q) ||
                (r.calibration_schedule?.equipment_name || '').toLowerCase().includes(q) ||
                (r.calibration_schedule?.equipment_code || '').toLowerCase().includes(q)
              );
            })}
            loading={recordsLoading}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={2} style={{ margin: 0 }}>
          Calibration Management
        </Title>
        <Button onClick={() => navigate('/qms/tmmde')}>Equipment List</Button>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Measuring equipment (API Q1)"
        description="Link schedules to assets from the fixed-asset register. Document calibration method, acceptance criteria, and as-found/as-left readings on records. Mark instruments used for conformity or process monitoring on the asset (Measuring equipment / ownership)."
      />

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="Total Schedules" value={stats.totalSchedules} />
          </Col>
          <Col span={6}>
            <Statistic title="Overdue" value={stats.overdue} valueStyle={{ color: '#cf1322' }} />
          </Col>
          <Col span={6}>
            <Statistic title="Total Records" value={stats.totalRecords} />
          </Col>
          <Col span={6}>
            <Statistic title="Passed" value={stats.passed} valueStyle={{ color: '#3f8600' }} />
          </Col>
        </Row>
      </Card>

      <Card>
        <Tabs items={tabItems} />
      </Card>

      <Modal
        title={scheduleView ? `Schedule — ${scheduleView.calibration_code}` : 'Schedule'}
        open={!!scheduleView}
        onCancel={() => setScheduleView(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setScheduleView(null)}>
            Close
          </Button>,
        ]}
        width={720}
        destroyOnHidden
      >
        {scheduleView && (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
            <Descriptions.Item label="Calibration code">{scheduleView.calibration_code}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(scheduleView.status)}>{scheduleView.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Asset code">{scheduleView.equipment_code || '—'}</Descriptions.Item>
            <Descriptions.Item label="Asset / equipment name">{scheduleView.equipment_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Method">{scheduleView.calibration_type || '—'}</Descriptions.Item>
            <Descriptions.Item label="Critical">{scheduleView.is_critical ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Standard / procedure" span={2}>
              {scheduleView.calibration_standard || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Acceptance criteria (Q1)" span={2}>
              {scheduleView.acceptance_criteria || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Frequency (days)">{scheduleView.calibration_frequency_days}</Descriptions.Item>
            <Descriptions.Item label="Provider">{scheduleView.calibration_provider || '—'}</Descriptions.Item>
            <Descriptions.Item label="Location" span={2}>
              {scheduleView.calibration_location || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Last calibration">
              {scheduleView.last_calibration_date
                ? dayjs(scheduleView.last_calibration_date).format('DD/MM/YYYY')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Next calibration">
              {dayjs(scheduleView.next_calibration_date).format('DD/MM/YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Due date">
              {dayjs(scheduleView.calibration_due_date).format('DD/MM/YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">{dayjs(scheduleView.updated_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CalibrationPage;

